import { motion } from 'framer-motion'
import styled, { css } from 'styled-components/macro'
import { mq } from 'styles/mq'

export const Header = styled.div<{ $visible?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  text-transform: uppercase;
  font-family: 'Skolar Sans PE Condensed', sans-serif;
  font-weight: 800;
  font-size: 0.8em;
  margin: 0 0 1.2em;
  transition: 0.3s opacity;

  ${props =>
    props.$visible === false &&
    css`
      pointer-events: none;
      opacity: 0;
    `}

  svg + span {
    width: max-content;
  }

  svg {
    display: block;
    margin-top: -0.2em;
  }
`

export const Title = styled.h2<{ hyphens?: boolean }>`
  font-size: 1.8em;
  line-height: 1.2;
  margin: 0 0 0.2em;
  color: white;
  font-weight: 800;
  font-family: 'Skolar Sans PE Condensed', sans-serif;

  ${props =>
    props.hyphens &&
    css`
      hyphens: auto;
    `}

  > span {
    padding: 0.3em 0.3em 0.24em;
    box-decoration-break: clone;
    background-color: #1a3948;

    > span {
      position: relative;
    }
  }
`

export const Description = styled.p`
  margin: 0;
  font-size: 0.64em;
  flex-grow: 1;
`

export const Dl = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.1em 0.3em;
  margin: 0;
  font-size: 0.7em;
  font-weight: bold;
  line-height: 1.2;
  text-transform: uppercase;

  dt,
  dd {
    display: inline;
    padding: 0;
    margin: 0;
  }

  dd {
    color: white;
  }
`

export const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  margin-block: auto;
`

export const Container = styled(CenterContent).attrs({
  role: 'button',
})<{ $variant: 'face' | 'reverse' | 'share'; $color?: string }>`
  position: relative;
  margin: 0;
  width: 100%;
  aspect-ratio: 1;
  padding: 2.8rem 2rem 2rem;
  font-size: 2rem;
  background-color: ${props => props.$color || '#eee'};

  &:is(button) {
    cursor: pointer;
  }

  ${mq.medium} {
    overflow: hidden;
  }

  &:focus-visible {
    outline: none;
  }

  ${props =>
    props.$variant === 'face' &&
    css`
      ${mq.smallOnly} {
        padding: 0.8em;
        font-size: 5vw;
      }

      ${Header} {
        ${mq.smallOnly} {
          display: none;
        }
      }

      ${Title} {
        margin: auto 0 0;
        font-size: 1.3em;
        line-height: 1;
        word-wrap: break-word;
        color: inherit;

        span {
          display: contents;
        }

        ${mq.medium} {
          margin: auto 0;
          font-size: 2em;
        }
      }

      ${Dl} {
        margin-top: 1em;

        ${mq.smallOnly} {
          display: none;
        }
      }
    `}

  ${props =>
    props.$variant === 'reverse' &&
    css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotateY(180deg);
      width: 200%;
      font-size: 1.2em;
      padding: 1.3em 1em 0.8em;

      ${Description} {
        font-size: 0.8em;

        ${mq.smallOnly} {
          font-size: 0.9em;
        }
      }

      ${Dl} {
        ${mq.smallOnly} {
          font-size: 0.8em;
        }
      }

      ${mq.medium} {
        font-size: 1.7em;
      }
    `}

  ${props =>
    props.$variant === 'share' &&
    css`
      position: fixed;
      top: 0;
      left: 100%;
      width: 16em;
      font-size: 2em;
      padding: 1.3em 1em 0.8em;

      ${mq.medium} {
        font-size: 4em;
      }
    `}
`

export const Share = styled(motion.span)<{ $expanded?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4em;
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.8em;
  border-bottom-left-radius: 1.4em;
  background-color: #b9cccc;

  svg {
    display: block;
    width: 1.2em;
    overflow: visible;
  }

  ${Container}:hover &,
  ${Container}:focus-visible & {
    color: white;
    background-color: #2e3a59;
  }

  ${props =>
    props.$expanded &&
    css`
      ${Container}:hover &,
      ${Container}:focus-visible & {
        background-color: #b9cccc;

        svg {
          color: #2e3a59;
        }
      }
    `}

  ${Container}:focus-visible & {
    outline: 5px solid white;
  }
`

export const MotionLi = styled(motion.li)<{
  $isReversed: boolean
  $isPrevious: boolean
}>`
  ${props =>
    !props.$isReversed &&
    css`
      cursor: pointer;
    `}

  ${props =>
    props.$isReversed &&
    css`
      z-index: 10;
    `}

  ${props =>
    props.$isPrevious &&
    css`
      z-index: 5;
    `}
`

export const Card = styled.div<{ isReversed: boolean }>`
  position: relative;
  display: flex;
  transition: transform 0.5s;
  transform-style: preserve-3d;
  flex-grow: 1;

  > * {
    flex-shrink: 0;
    backface-visibility: hidden;
  }

  ${props =>
    props.isReversed &&
    css`
      transform: rotateY(180deg);
    `}
`

export const Social = styled(motion.span)`
  display: flex;

  button {
    all: unset;
    margin-left: 0.2em;

    svg {
      font-size: 0.6em;
    }
  }

  a {
    color: inherit;
  }

  svg {
    width: 1.2em;
    margin: -0.04em 0.2em;
  }
`

export const ShareToggle = styled(motion.button)`
  all: unset;
  cursor: pointer;
`
